import React, { useState } from 'react';
import { Table, Tag, Button, Space, Typography, Card, message, Tooltip, Popconfirm, Flex } from 'antd';
import { 
    ReloadOutlined, 
    DeleteOutlined, 
    CheckCircleOutlined, 
    ClockCircleOutlined, 
    ExclamationCircleOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type Notification } from '../../api/notifications';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const NotificationsList: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const queryClient = useQueryClient();

    const { data: response, isLoading } = useQuery({
        queryKey: ['notifications', page, pageSize],
        queryFn: () => notificationsApi.getNotifications({ pageNumber: page, pageSize: pageSize })
    });

    const retryMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.retryNotification(id),
        onSuccess: () => {
            message.success('Notification queued for retry');
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.deleteNotification(id),
        onSuccess: () => {
            message.success('Notification deleted');
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const getStatusTag = (processedAt?: string, error?: string) => {
        if (error) return <Tag color="error" icon={<ExclamationCircleOutlined />}>FAILED</Tag>;
        if (processedAt) return <Tag color="success" icon={<CheckCircleOutlined />}>PROCESSED</Tag>;
        return <Tag color="processing" icon={<ClockCircleOutlined />}>PENDING</Tag>;
    };

    const getPriorityTag = (priority: any) => {
        const colors: Record<string, string> = {
            'Low': 'default',
            'Normal': 'blue',
            'High': 'orange',
            'Critical': 'red',
            // Handle numeric enums if they come as numbers
            '0': 'default',
            '1': 'blue',
            '2': 'orange',
            '3': 'red'
        };

        const priorityStr = priority?.toString() || 'Normal';
        const label = isNaN(Number(priority)) 
            ? priorityStr.toUpperCase() 
            : ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'][Number(priority)] || 'NORMAL';

        return <Tag color={colors[priorityStr] || 'blue'}>{label}</Tag>;
    };

    const columns = [
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
            width: 150
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text: string) => <Text strong>{text}</Text>
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (priority: string) => getPriorityTag(priority),
            width: 100
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: Notification) => getStatusTag(record.processedAt, record.error),
            width: 120
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Notification) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button 
                            icon={<EyeOutlined />} 
                            onClick={() => {
                                // For now just show message in modal/alert if needed
                                message.info(record.message);
                            }}
                        />
                    </Tooltip>
                    {!record.processedAt && (
                        <Popconfirm 
                            title="Retry sending?" 
                            onConfirm={() => retryMutation.mutate(record.id)}
                        >
                            <Button icon={<ReloadOutlined />} loading={retryMutation.isPending} />
                        </Popconfirm>
                    )}
                    <Popconfirm 
                        title="Delete notification?" 
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText="Yes" 
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending} />
                    </Popconfirm>
                </Space>
            ),
            width: 150
        }
    ];

    return (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Flex justify="space-between" align="center">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>Notification Queue</Title>
                    <Text type="secondary">Monitor and manage administrative alerts.</Text>
                </div>
                <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['notifications'] })}
                >
                    Refresh
                </Button>
            </Flex>

            <Card variant="borderless">
                <Table 
                    columns={columns} 
                    dataSource={response?.data || []} 
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: response?.totalRecords || 0,
                        onChange: (p) => setPage(p)
                    }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ margin: 0 }}>
                                <p><strong>Recipient:</strong> {record.recipient || 'N/A'}</p>
                                <p><strong>Message:</strong></p>
                                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                                    {record.message}
                                </pre>
                                {record.error && (
                                    <p style={{ color: '#ff4d4f' }}><strong>Error:</strong> {record.error}</p>
                                )}
                            </div>
                        )
                    }}
                />
            </Card>
        </Space>
    );
};
